import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Commercial & Long-Term Leasing", item: "https://cashmyproperty.ai/services/leasing" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Do I need to construct anything on my land for leasing?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. In Built-to-Suit leasing, the tenant (warehouse operator, QSR brand, or corporate) constructs their own facility at their own cost on your land. You simply provide the land and collect monthly rent — zero construction investment from your side. The structure typically becomes yours after the lease term ends.",
      },
    },
    {
      "@type": "Question",
      name: "What kind of monthly rent can I expect?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Rent varies by location and land type. Highway-fronting plots for warehousing typically yield ₹8–25 per sq.ft/month. Retail/QSR placements can yield ₹25–80 per sq.ft/month. Industrial leases average ₹5–15 per sq.ft/month. Our AI feasibility report provides a precise rent estimate based on your specific location.",
      },
    },
    {
      "@type": "Question",
      name: "What is the typical lease duration?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Commercial leases through our platform are typically 9–15 years with 5–10% rent escalation every 3 years. Longer leases provide income stability and are preferred by institutional tenants. We negotiate lock-in periods (typically 3–5 years) so the tenant cannot leave early without penalty.",
      },
    },
    {
      "@type": "Question",
      name: "What happens to the structure after the lease ends?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "In most Built-to-Suit agreements, the constructed facility becomes your property after the lease term expires. You can then renew the lease with the existing tenant, lease it to a new tenant, or repurpose it — all while retaining the value of the building that was built at zero cost to you.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Land Suitability Analysis",
    desc: "Our AI analyzes your plot's location, road access, proximity to highways, industrial zones, and demand heat maps. We identify the top 3 leasing use cases ranked by ROI potential.",
    icon: "ri-radar-line",
  },
  {
    step: "02",
    title: "Tenant Matching",
    desc: "We shortlist corporate tenants actively seeking land in your area — warehouse operators (Delhivery, Amazon), QSR chains, EV charging networks, data centers, and retail brands.",
    icon: "ri-building-line",
  },
  {
    step: "03",
    title: "Term Sheet Negotiation",
    desc: "Our team negotiates rent rates, lease duration, escalation clauses, lock-in periods, and maintenance terms. You approve before anything is signed.",
    icon: "ri-file-list-3-line",
  },
  {
    step: "04",
    title: "Lease Registration",
    desc: "Legal team drafts and registers the lease deed. All stamp duty, registration, and compliance formalities are handled with full transparency.",
    icon: "ri-file-check-line",
  },
  {
    step: "05",
    title: "Tenant Builds, You Earn",
    desc: "Tenant constructs their facility (3–6 months for most). Rent starts flowing monthly into your account. We provide ongoing asset management for the lease duration.",
    icon: "ri-money-rupee-circle-line",
  },
];

const benefits = [
  { icon: "ri-bank-line", title: "Stable Monthly Income", desc: "Predictable rent deposited monthly for 9–15 years. Typical ROI of 6–10% per annum on land value — far exceeding FD returns." },
  { icon: "ri-tools-line", title: "Tenant Builds Everything", desc: "The corporate tenant funds and constructs their own facility. You incur zero capital expenditure. The building becomes yours after lease expiry." },
  { icon: "ri-shield-star-line", title: "Anti-Encroachment Protection", desc: "A leased and occupied property is actively protected. No squatters, no encroachment, no boundary disputes — your land stays visibly occupied and secure." },
  { icon: "ri-arrow-up-circle-line", title: "Value Appreciation", desc: "Land with a leased commercial facility is worth 40–60% more than vacant land. You build equity while earning income." },
  { icon: "ri-file-shield-2-line", title: "Fully Managed", desc: "We handle tenant sourcing, negotiation, legal registration, rent collection, and ongoing tenant management. You just receive income updates." },
  { icon: "ri-refresh-line", title: "Rent Escalation Built-In", desc: "5–10% rent increase every 3 years is standard in our leases. Your income grows while your costs stay at zero." },
];

const faqs = [
  { q: "Do I need to construct anything on my land for leasing?", a: "No. In Built-to-Suit leasing, the tenant (warehouse operator, QSR brand, or corporate) constructs their own facility at their own cost on your land. You simply provide the land and collect monthly rent — zero construction investment from your side. The structure typically becomes yours after the lease term ends." },
  { q: "What kind of monthly rent can I expect?", a: "Rent varies by location and land type. Highway-fronting plots for warehousing typically yield ₹8–25 per sq.ft/month. Retail/QSR placements can yield ₹25–80 per sq.ft/month. Industrial leases average ₹5–15 per sq.ft/month. Our AI feasibility report provides a precise rent estimate based on your specific location." },
  { q: "What is the typical lease duration?", a: "Commercial leases through our platform are typically 9–15 years with 5–10% rent escalation every 3 years. Longer leases provide income stability and are preferred by institutional tenants. We negotiate lock-in periods (typically 3–5 years) so the tenant cannot leave early without penalty." },
  { q: "What happens to the structure after the lease ends?", a: "In most Built-to-Suit agreements, the constructed facility becomes your property after the lease term expires. You can then renew the lease with the existing tenant, lease it to a new tenant, or repurpose it — all while retaining the value of the building that was built at zero cost to you." },
];

export default function LeasingServicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Commercial Land Leasing — Earn Monthly Rent From Idle Land | CashMyProperty.ai",
    description: "Turn your idle land into a monthly income stream with 9–15 year Built-to-Suit commercial leases. Warehouse, logistics, QSR, EV charging, and data center tenants build at their cost. You earn 6–10% annual ROI with zero capital investment. Full tenant management included.",
    canonicalUrl: "https://cashmyproperty.ai/services/leasing",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Large%20modern%20logistics%20warehouse%20and%20industrial%20park%20at%20golden%20hour%2C%20trucks%20docked%20at%20loading%20bays%2C%20dramatic%20warm%20amber%20sky%2C%20wide%20establishing%20shot%2C%20cinematic%20industrial%20photography%2C%20deep%20teal%20and%20gold%20color%20palette%2C%20India%20industrial%20development%2C%20highly%20detailed%20architectural%20photography&width=1800&height=800&seq=service-detail-leasing-hero&orientation=landscape"
          alt="Commercial Land Leasing — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Commercial &</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Long-Term Leasing</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Your land earns monthly rent for 9–15 years. Zero construction cost — the tenant builds everything.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Commercial & Long-Term Leasing</span>
          </nav>
        </div>
      </section>

      {/* What is Leasing */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What is Built-to-Suit Leasing?</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Your Land Becomes a Monthly Paycheck
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Commercial leasing is the most predictable monetization path. Major corporations, logistics companies, and retail chains are constantly hunting for well-located land — and they are willing to build their own facilities on it.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Under a Built-to-Suit lease, the tenant designs and constructs their facility at <strong className="text-foreground-950">their own cost</strong>. You simply provide the land and collect monthly rent — typically yielding <strong className="text-foreground-950">6–10% annual ROI</strong> on your land value. That is 2–3x what bank FDs offer.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                At the end of the 9–15 year lease term, the constructed facility typically becomes <strong className="text-foreground-950">your property</strong> — adding massive capital value on top of the rental income you have already earned.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20warehouse%20logistics%20facility%20with%20corporate%20branding%2C%20trucks%20and%20containers%2C%20wide%20angle%20view%2C%20dramatic%20dusk%20sky%2C%20warm%20amber%20loading%20bay%20lights%2C%20cinematic%20industrial%20photography%2C%20deep%20teal%20sky%2C%20India%20logistics%20infrastructure%2C%20high%20detail&width=800&height=600&seq=leasing-warehouse-01&orientation=landscape"
                alt="Commercial Leasing"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">From Empty Plot to Monthly Rent</h2>
            <p className="text-foreground-600">Five steps. Zero construction cost. Income starts within 3–6 months.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tenant Types */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Who Needs Your Land</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Active Tenant Categories</h2>
            <p className="text-foreground-600">Corporations actively seeking land across India right now.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: "ri-truck-line", title: "Warehousing & Logistics", brands: "Delhivery, Amazon, Flipkart, Blue Dart", rent: "₹8–25/sq.ft/month" },
              { icon: "ri-store-2-line", title: "QSR & Retail Chains", brands: "McDonald's, Starbucks, KFC, Domino's", rent: "₹25–80/sq.ft/month" },
              { icon: "ri-database-2-line", title: "Data Centers", brands: "AWS, Yotta, CtrlS, NTT", rent: "₹15–40/sq.ft/month" },
              { icon: "ri-plug-line", title: "EV Charging Hubs", brands: "Tata Power, Jio-bp, Ather, Statiq", rent: "₹10–30/sq.ft/month" },
              { icon: "ri-shopping-bag-3-line", title: "Dark Stores & Q-Commerce", brands: "Zepto, Blinkit, Swiggy Instamart", rent: "₹15–35/sq.ft/month" },
              { icon: "ri-building-line", title: "Co-Working & Offices", brands: "WeWork, Incuspaze, Awfis", rent: "₹20–50/sq.ft/month" },
            ].map((cat) => (
              <div key={cat.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${cat.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{cat.title}</h3>
                <p className="text-xs text-foreground-500 mb-3">{cat.brands}</p>
                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary-500/10 text-primary-700">{cat.rent}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Leasing Beats Selling</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "9–15 Yrs", label: "Lease Duration" },
              { value: "6–10%", label: "Annual ROI" },
              { value: "3–6 Mo", label: "Time to First Rent" },
              { value: "₹0", label: "Your Construction Cost" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Turn Your Land Into Monthly Income?
          </h2>
          <p className="text-foreground-600 mb-8">
            Our AI will calculate exactly how much rent your plot can generate — and which tenants are looking for land like yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Rent Estimate — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}